/** @jsxImportSource @emotion/react */
import { useCallback, useContext, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { ErrorBox } from 'components/ErrorBox';
import { Select, Submit } from 'components/Form';
import { PluginContext } from 'context/Plugin';
import { RedirectContext } from 'context/Redirect';
import { StackRegionContext } from 'context/StackRegion';
import { TrackingContext } from 'context/Tracking';
import { UserContext } from 'context/User';
import { useEffectOnce } from 'hooks/useEffectOnce';
import { Endpoints, get, post, retry, StackReadinessResponse, TrialAndStackStartResponse } from 'utils/api';
import { getLegalTermsVersion } from 'utils/legalTerms';
import { getStackInstanceUrl, isStackReadyFromStart } from 'utils/signupStack';

interface SelectOrganisationFormValues {
  region: string;
}

const formName = 'create-organisation';

const waitForStackReady = async (stackSlug: string): Promise<void> => {
  await retry(
    async () => {
      const readinessResult = await get<StackReadinessResponse>(
        `${Endpoints.GET_STACK_READINESS}/${stackSlug}/readiness`
      );
      if (!readinessResult.ready) {
        throw new Error('Your stack was created but was not able to start. Please contact support.');
      }
    },
    120,
    1_000
  );
};

const resolveTrialStartResponse = async (trialRes: TrialAndStackStartResponse): Promise<TrialAndStackStartResponse> => {
  if (isStackReadyFromStart(trialRes.stack)) {
    return trialRes;
  }

  await waitForStackReady(trialRes.stack.slug);

  return {
    ...trialRes,
    stack: {
      ...trialRes.stack,
      url: getStackInstanceUrl(trialRes.stack),
      ready: true,
    },
  };
};

export const SelectOrganisationForm = () => {
  const [, { getUser, setUser }] = useContext(UserContext);
  const [{ stackRegionsData }] = useContext(StackRegionContext);
  const { plugin } = useContext(PluginContext);
  const [, { faro, trackEvent }] = useContext(TrackingContext);
  const [serverError, setServerError] = useState<string>();
  const [{ to, customContent }, { redirectToComputedInstanceOnboarding, redirectToTo }] = useContext(RedirectContext);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors },
    setValue,
  } = useForm<SelectOrganisationFormValues>({ mode: 'onBlur' });

  useEffectOnce(() => {
    setValue('region', stackRegionsData?.length ? stackRegionsData[0].slug : '');
  });

  // Fire-and-forget: SSO signups record terms here (first chance on the client). Email/password signups
  // already post acceptance in CreateUserForm; a duplicate accept returns Conflict and is ignored below.
  useEffectOnce(() => {
    void (async () => {
      try {
        const legalTermsVersion = await getLegalTermsVersion();
        if (legalTermsVersion) {
          await post(Endpoints.ACCEPT_LEGAL_TERMS, { legalTermsVersion });
        }
      } catch (err) {
        const isConflict =
          typeof err === 'object' && err !== null && 'code' in err && (err as { code: string }).code === 'Conflict';
        if (isConflict) {
          // user acceptance terms have already been recorded and our request will not duplicate the data
        } else {
          faro.trackError(err);
        }
      }
    })();
  });

  const handleFormSubmit: SubmitHandler<SelectOrganisationFormValues> = useCallback(
    async (values) => {
      setServerError(undefined);

      const pluginPayload = {
        ...(plugin && { plugins: [plugin.slug] }),
      };

      try {
        faro.trackFormSubmit(formName);

        const trialRes = await post<TrialAndStackStartResponse>(Endpoints.TRIAL_AND_STACK_START, {
          region: values.region,
          ...pluginPayload,
        });
        const stackReadyFromStart = isStackReadyFromStart(trialRes.stack);
        const resolvedTrial = await resolveTrialStartResponse(trialRes);

        const newUserData = await getUser();

        await setUser(newUserData);

        faro.trackFormSubmitSuccess(formName);

        if (to && !customContent) {
          redirectToTo();
        } else {
          redirectToComputedInstanceOnboarding(newUserData!, { trialStart: resolvedTrial, stackReadyFromStart });
        }

        trackEvent({
          name: `finish-create-stack`,
        });
      } catch (err) {
        const message = err.message;
        faro.trackFormSubmitError(formName, err.message);
        faro.trackError(err);

        trackEvent({
          name: `finish-create-stack-fail`,
          error: message,
        });
        setServerError(message);
      }
    },
    [customContent, faro, getUser, plugin, redirectToComputedInstanceOnboarding, redirectToTo, setUser, to, trackEvent]
  );

  return (
    <form id="form" noValidate onSubmit={handleSubmit(handleFormSubmit)}>
      {!!stackRegionsData?.length && (
        <Select
          control={control}
          disabled={isSubmitting}
          errors={errors}
          id="region"
          label="Deployment region"
          name="region"
          options={stackRegionsData.map((region) => ({ label: region.publicName ?? region.name, value: region.slug }))}
          showLabel
          infoTooltip={'Select a region close to your users for the best performance.'}
        />
      )}
      <Submit
        disabled={isSubmitting}
        id="submit"
        isLoading={isSubmitting}
        label={'Finish Setup'}
        loadingText={'Loading'}
      />
      {serverError && <ErrorBox id="server-error">{serverError}</ErrorBox>}
    </form>
  );
};
